import React, { useEffect } from "react";
import * as Sentry from "@sentry/react";
import { matchPath } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { createBrowserHistory } from "history"; // only upgrade to v5 once react-router is upgraded to v6
import packageJson from "package.alias.json";
import NavRoutes from "core/app/util/NavRoutes";
import {
  browserLanguage,
  getSelectedLanguage,
} from "settings/util/localizationSettings";

const T1NEXT_PROJECT_ID =
  "https://00443d4fd31c4d2ea6fd62ea36bd65a8@o1043010.ingest.sentry.io/6054776";
const USER_ID_URL = "https://api.mediamath.com/user_id";

export const history = createBrowserHistory();
const sentryReservedWords = ["id"];
const testUserIDs = [37832, 37833];
const prodURLs = ["platform.mediamath.com", "newt1.mediamath.com"];
const uatURLs = ["newt1-uat.mediamath.com", "uat-platform.mediamath.com"];
const getTz = () => Intl.DateTimeFormat().resolvedOptions().timeZone;
const isLocalDev = (host) => ["localhost", "127.0.0.1"].includes(host);
const isJest = () => process.env.JEST_WORKER_ID !== undefined;
const isE2E = () => Boolean(navigator.webdriver);
const isProd = (host) => prodURLs.includes(host);
const isUat = (host) => uatURLs.includes(host);

export const getEnvironment = (hostname) => {
  if (isLocalDev(hostname)) return "dev";
  if (hostname.includes("pr")) return "pr";
  if (hostname.includes("qa")) return "qa";
  if (isUat(hostname)) return "uat";
  if (isProd(hostname)) return "production";
  return "unknown";
};

export const getRouteList = () => {
  return Object.values(NavRoutes).flatMap((section) => Object.values(section));
};

export const getMatchPath = (pathname, routes) => {
  const match = routes.find((path) => matchPath(pathname, { path }));
  return match || null;
};

export const getSection = (pathname) => {
  if (
    pathname.startsWith("/campaigns") ||
    pathname.startsWith("/strategies") ||
    pathname.startsWith("/h/campaign") ||
    pathname.startsWith("/h/strategy")
  ) {
    return "Campaigns";
  }

  if (pathname.startsWith("/inventory")) return "Inventory";
  if (pathname.startsWith("/creative")) return "Creatives";
  if (pathname.startsWith("/strategy_plans")) return "Plans";

  if (
    pathname.startsWith("/admin") ||
    pathname.startsWith("/enterprise-controls")
  ) {
    return "Admin";
  }

  return "unknown";
};

export const addParamPrefix = (params = {}) => {
  return Object.entries(params).reduce((acc, [key, value]) => {
    acc[sentryReservedWords.includes(key) ? `param_${key}` : key] = value;
    return acc;
  }, {});
};

export const getBaseTags = (routes) => {
  const { pathname, search } = window.location;
  const urlParams = new URLSearchParams(search);
  const params = Object.fromEntries(urlParams.entries());
  const match = getMatchPath(pathname, routes);
  const section = getSection(pathname);
  const tags = { section, ...addParamPrefix(params) };

  return match ? { ...tags, ...addParamPrefix(match.params) } : tags;
};

const env = getEnvironment(window.location.hostname);
// Do not use Sentry while testing to keep quota in check
const useSentry = !(env === "dev" || env === "pr" || isJest() || isE2E());

if (useSentry) {
  const routes = getRouteList();

  Sentry.init({
    // Filter/update trail of breadcrumbs
    beforeBreadcrumb(breadcrumb, hint) {
      // Filter out spammy console
      if (breadcrumb.category === "console") return null;
      // Filter out LaunchDarkly events
      if (breadcrumb.data?.url?.includes("launchdarkly.com")) return null;
      // Filter out Sentry events
      if (breadcrumb.data?.url?.includes("sentry.io")) return null;
      // Filter out third-party tracking tags
      if (breadcrumb.data?.url?.includes("securepubads")) return null;
      // Filter out third-party tracking tags
      if (breadcrumb.data?.url?.startsWith("data:image/svg+xml")) return null;
      // Filter out cancelled errors, likely due to React rendering issues which
      // are easy to spot in the network tab. Do not waste replays on this.
      if (breadcrumb.message === "CanceledError: canceled") return null;

      // Annotate UI click
      if (breadcrumb.category === "ui.click") {
        const tgt = hint.event.target;
        const tgtTestId = tgt.getAttribute("data-testid");
        const tgtInnerText = tgt.innerText;
        const tgtNodeName = tgt.nodeName;
        const testNode = tgtTestId || tgtInnerText;
        const message = testNode
          ? `${tgtNodeName}: ${testNode}`
          : breadcrumb.message;

        return { ...breadcrumb, message };
      }

      return breadcrumb;
    },
    // Filter message and error events
    beforeSend(evt) {
      // Filter out Compass events
      if (evt.request?.url?.includes("/c/")) return null;
      // Ignore cancelled errors, likely due to React rendering issues which
      // are easy to spot in the network tab. Do not waste replays on this.
      if (evt.exception?.values?.[0]?.type === "CanceledError") return null;

      return evt;
    },
    // Filter transaction events
    beforeSendTransaction(evt) {
      // Filter out test users
      if (testUserIDs.includes(evt.user?.id)) return null;
      // Filter out Compass events
      if (evt.request?.url?.includes("/c/")) return null;
      // Attach dynamic tags
      const tags = { ...evt.tags, ...getBaseTags(routes) };

      return { ...evt, tags };
    },
    // Ignore Compass errors
    denyUrls: [/.*\/c\/*.*/i],
    // Project ID in the Sentry UI
    dsn: T1NEXT_PROJECT_ID,
    // Environment the app is running in
    environment: env,
    // Ignore auth errors
    ignoreErrors: [
      "Error new t(@auth0/auth0-react/dist/auth0-react.esm)",
      "Login required",
    ],
    // Provide initial tags that won't be changing
    initialScope: {
      tags: {
        "browser.locale": browserLanguage,
        "user.language": getSelectedLanguage(),
        "user.tz": getTz(),
      },
    },
    // Track page loads and navigation
    integrations: [
      // Needs to be upgraded once react-router is upgraded to v6
      Sentry.reactRouterV5BrowserTracingIntegration({ history }),
      Sentry.replayIntegration(),
    ],
    // Attach release version
    release: `t1next@${packageJson.version || "dev"}`,
    // Do not capture successful sessions for Replay. We only have limited quota.
    replaysSessionSampleRate: 0,
    // Capture Replay for 10% of sessions with an error. Adjust according to quota.
    replaysOnErrorSampleRate: 0.1,
    // Rate of error events (1.0 = 100%). Reduce rate in envs other than Prod.
    sampleRate: env === "production" ? 1.0 : 0.3,
    // Rate of transaction events (1.0 = 100%). Reduce rate in envs other than Prod.
    tracesSampler: ({ attributes = {}, name = "" }) => {
      // Do not trace Compass
      if (attributes["sentry.source"] === "url" && name.includes("/c/"))
        return 0;

      return env === "production" ? 1.0 : 0.3;
    },
    // Trace only BFF calls
    tracePropagationTargets: [/^\/marvell\//],
  });
}

// Once we have a user ID, update Sentry
export const SentryUserContext = () => {
  const { user } = useAuth0();
  const userId = user ? user[USER_ID_URL] : null;

  useEffect(() => {
    if (userId) Sentry.setUser({ id: userId });
  }, [userId]);

  return <React.Fragment />;
};

export default Sentry;
